import { useCallback, useEffect, useMemo } from 'react';

import { PlayerSupportedFeatures } from 'application/pages/constants';
import { DEFAULT_POINT_STATUSES } from 'application/pages/PointsPage/renderer/PointsToolbar/consts';
import { useAppSelector } from '@shared/store';
import { selectCompanyIds } from '@entities/account';

import { AdCampaignMediaTypeEnum } from '@shared/api/graphql/generated';
import { AdCampaignTypeEnum } from '@entities/media';
import { useAllPointList } from '@entities/point';

import { selectCampaignType } from '../../../slices/createAdCampaign/selectors';
import type { PointOption } from './types';

type UseAdCampaignStepperPointsViewModelProps = {
  mediaType?: AdCampaignMediaTypeEnum;
};

export const useAdCampaignStepperPointsViewModel = ({
  mediaType = AdCampaignMediaTypeEnum.Audio,
}: UseAdCampaignStepperPointsViewModelProps) => {
  const companyIds = useAppSelector(selectCompanyIds);
  const campaignType = useAppSelector(selectCampaignType);
  const isInstant = campaignType === AdCampaignTypeEnum.Instant;
  const hasVideoSupport = mediaType === AdCampaignMediaTypeEnum.Video ? true : undefined;

  const allPointList = useAllPointList({
    companyIds,
    playingStatuses: DEFAULT_POINT_STATUSES,
    hasVideoSupport,
    supportedFeatures: isInstant ? [PlayerSupportedFeatures.AdInstant] : undefined,
    withDevices: isInstant ? true : undefined,
  });

  useEffect(() => {
    if (companyIds.length > 0) {
      allPointList.request();
    }
  }, [campaignType, companyIds, mediaType]);

  const loadMore = useCallback(async () => {
    if (allPointList.loading || !allPointList.paginationInfo?.hasNextPage) {
      return;
    }

    allPointList.fetchMore(allPointList.paginationInfo?.page);
  }, [allPointList]);

  const points = useMemo<PointOption[]>(() => {
    const loadedPoints = allPointList.points || [];

    if (isInstant) {
      return loadedPoints.filter((point) =>
        point.device?.supportedFeatures.includes(PlayerSupportedFeatures.AdInstant)
      );
    }

    return loadedPoints;
  }, [allPointList.points, isInstant]);

  return {
    isLoading: allPointList.isReloading,
    loadMore,
    points,
  };
};
